/**
 * @file Telink 键盘与接收器的版本读取、在线检查和 OTA 升级能力。
 *
 * 协议来源：
 * - 《Telink多模游戏键盘V700ProUSB通信协议》：键盘版本读取与进入升级模式命令。
 * - 《VT系列游戏接收器端USB通信协议》：接收器版本读取与进入升级模式命令。
 * - RapooSystem 的 FirmwareUpgradeTelink.cpp：Bootloader 的启动、分包、结束帧和 CRC16 格式。
 *
 * WebHID 适配说明：原生 HID 缓冲区包含 Report ID；WebHID 将 Report ID 作为独立参数传入，
 * 所以本文件构造的 Bootloader 数据区不再包含 Report ID 字节。
 */

import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { ConnectionType } from '../../../../types/connection'
import {
  DeviceType,
  RemoteDeviceVersion,
  VersionInfo,
  VersionType,
} from '../../../../types/firmware'
import { delay } from '../../../../utils/TimeUtils'
import { FirmwareEvents } from '../../../../../src/events/FirmwareEvents'
import { processZip } from '../../../shared/firmware/shared/FileUtil'
import {
  createTelinkFirmwareUpdateEntryCommand,
  createTelinkKeyboardControlPayload,
  createTelinkKeyboardFirmwareVersionCommand,
  TELINK_KEYBOARD_COMMAND,
  TELINK_KEYBOARD_CONTROL_STATUS,
  TELINK_KEYBOARD_HID,
} from '../../protocols/keyboard-command'

const VERSION_FILE_NAME = 'version.json'
// 来源：FirmwareUpgradeTelink.cpp::run()。设备切换升级程序后需要稳定时间。
// 当前沿用固定等待；后续若设备支持可靠的连接事件，应优先改为轮询升级接口出现。
const OTA_BOOT_WAIT_MS = 3000
// 来源：浏览器读取到的设备 HID 描述符。Bootloader 的 Report ID 与业务接口的 0xBA 独立。
const BOOTLOADER_REPORT_ID = 0x05
// 来源：FirmwareUpgradeTelink.cpp::ota_write() 发送 64 字节，其中首字节为 0x05。
// WebHID 将该首字节作为 sendReport() 的第一个参数，因此数据区只保留后续 63 字节。
const BOOTLOADER_REPORT_DATA_SIZE = 63
// 来源：FirmwareUpgradeTelink.cpp::ota_write()。固件按固定 16 字节块编号，CRC 覆盖一个块。
const OTA_BLOCK_SIZE = 16
// 来源：FirmwareUpgradeTelink.cpp::ota_write()。每条记录 20 字节，63 字节数据区最多容纳三条。
const OTA_BLOCKS_PER_REPORT = 3
// 记录格式：块序号 2 字节 + 固件数据 16 字节 + Modbus CRC16 2 字节。
const OTA_BLOCK_RECORD_SIZE = 20

/** Telink 在线版本文件的最小结构。 */
type TelinkFirmwareVersionFile = {
  Device?: RemoteDeviceVersion[]
}

type FirmwareUpdateStage = 'downloading' | 'downloaded' | 'bootloader' | 'writing' | 'written'

/** Telink 键盘和接收器的固件能力。 */
export class TelinkKeyboardFirmware extends FirmwareFeature {
  /**
   * 读取当前连接链路可访问的固件版本。
   *
   * 有线时返回键盘版本；无线接收器连接时同时返回接收器与已配对键盘版本。
   * 配对键盘版本仅用于展示，在线检查和升级仍以当前连接目标为准。
   */
  public async getFirmwareVersion(): Promise<VersionInfo[]> {
    // 无线时主通道属于接收器，不能把接收器返回的版本误标记为键盘版本。
    if (this.isReceiverConnection()) {
      const receiverVersion = await this.readReceiverFirmwareVersion()
      try {
        // 接收器支持透传读取已配对键盘版本；两个版本一起返回，供 Web 分卡展示。
        return [receiverVersion, await this.readKeyboardFirmwareVersion(true)]
      } catch (error) {
        // 键盘未在线不应阻断接收器版本查询和接收器升级。
        console.warn('[TelinkKeyboardFirmware] 无法读取配对键盘固件版本', error)
        return [receiverVersion]
      }
    }

    return [await this.readKeyboardFirmwareVersion(false)]
  }

  /**
   * 通过已配对接收器读取键盘版本。
   *
   * 这是 V700 无线透传的附加诊断能力，不参与标准在线升级目标判断，避免键盘未在线时阻断接收器升级。
   */
  public async getPairedKeyboardFirmwareVersion(): Promise<VersionInfo> {
    if (!this.isReceiverConnection()) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '仅接收器连接支持透传读取键盘固件版本')
    }
    return this.readKeyboardFirmwareVersion(true)
  }

  /**
   * 对当前链路可升级的目标标记在线版本状态。
   *
   * 键盘固件按 mainControl 获取，接收器固件按 dongleModel 获取，避免把同一版本文件误用于两个芯片。
   */
  public async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const versions = await this.getFirmwareVersion()
    const updateTargetType = this.isReceiverConnection() ? DeviceType.Receiver : DeviceType.Keyboard
    const release = await this.getVersionInfo(
      updateTargetType === DeviceType.Receiver
        ? this.device.manifest.dongleModel
        : this.device.manifest.mainControl,
    )

    const keyboardRelease = this.isReceiverConnection()
      ? await this.getVersionInfo(this.device.manifest.mainControl).catch(() => null)
      : release

    return versions.map((version) => {
      // 无线时键盘版本只用于展示；键盘升级必须使用有线链路。
      if (version.deviceType !== updateTargetType) {
        return { ...version, onlineVersion: keyboardRelease?.FirmwareVersion, canUpdate: false }
      }
      const onlineVersion = release?.FirmwareVersion
      return {
        ...version,
        // 版本文件的原始值需要透传给 Web 层，便于现场核对本机版本与实际下载目标。
        onlineVersion,
        canUpdate: this.canUpdateByVersion(onlineVersion, version.version),
      }
    })
  }

  /**
   * 升级当前连接链路对应的一个目标。
   *
   * 调用顺序：下载固件 → 进入升级程序 → 打开升级接口 → 发送 OTA 帧。
   * 键盘 Bootloader 只能通过有线 USB 传输；无线时只升级接收器自身，避免 RF 链路中断后继续向键盘写入。
   */
  public async updateFirmware(): Promise<void> {
    // 一个连接只升级当前可直达的目标，避免无线接收器升级时误向键盘写数据。
    const targetType = this.isReceiverConnection() ? DeviceType.Receiver : DeviceType.Keyboard
    const versions = await this.checkFirmwareUpdate()
    const target = versions.find(
      (version) => version.deviceType === targetType && version.canUpdate,
    )
    if (!target) return

    // 键盘与接收器的在线包分目录存放；这里决定后续 version.json 和 ZIP 的查询目录。
    const model =
      targetType === DeviceType.Keyboard
        ? this.device.manifest.mainControl
        : this.device.manifest.dongleModel
    const release = await this.getVersionInfo(model)
    if (!release?.FirmwareURL) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件下载地址为空')
    }

    // 下载成功后才允许设备切入升级程序，避免设备已进入 Bootloader 但主机没有可写入的数据。
    this.reportStage(
      'downloading',
      `正在下载固件包（线上版本：${release.FirmwareVersion ?? '未提供'}）`,
    )
    const firmware = await this.downloadFirmware(model, release.FirmwareURL)
    this.reportStage(
      'downloaded',
      `固件包下载完成（版本：${release.FirmwareVersion ?? '未提供'}，文件：${release.FirmwareURL}，BIN：${firmware.byteLength} 字节）`,
    )
    this.reportStage('bootloader', '正在进入 Bootloader')
    await this.enterFirmwareUpdateMode(targetType === DeviceType.Receiver)
    await delay(OTA_BOOT_WAIT_MS)

    // 升级通道按 manifest 的 usageType 查找，最终匹配 HID 描述符中的 FFEF:00 接口。
    const updateDevice = await this.device.session.channels.openDevice({ usageType: 'update' })
    if (!updateDevice) {
      throw new DeviceError(DeviceErrorCode.OPEN_FAILED, '未发现 Telink Bootloader 升级接口')
    }

    try {
      this.reportStage('writing', 'Bootloader 已就绪，正在写入固件', 0)
      await this.writeFirmware(updateDevice, firmware)
      this.reportStage('written', '固件写入完成，已发送设备重启帧', 100)
    } finally {
      await this.device.session.channels.releaseDevice(updateDevice)
    }
  }

  /** 读取接收器 BCD 版本；该命令不经过键盘透传。 */
  private async readReceiverFirmwareVersion(): Promise<VersionInfo> {
    const response = await this.sendControlCommand(
      new Uint8Array([TELINK_KEYBOARD_COMMAND.readReceiverFirmwareVersion]),
      4,
      '读取 Telink 接收器固件版本失败',
    )
    return {
      version: `${response.getUint8(1)}.${response.getUint8(2)}.${response.getUint8(3)}`,
      versionType: VersionType.Firmware,
      deviceType: DeviceType.Receiver,
    }
  }

  /** 按 V700 文档读取键盘 BCD 版本；无线时由接收器转发命令。 */
  private async readKeyboardFirmwareVersion(isWireless: boolean): Promise<VersionInfo> {
    const response = await this.sendControlCommand(
      createTelinkKeyboardFirmwareVersionCommand(isWireless),
      5,
      '读取 Telink 键盘固件版本失败',
    )
    return {
      version: this.formatKeyboardVersion(response),
      versionType: VersionType.MasterControl,
      deviceType: DeviceType.Keyboard,
    }
  }

  /** 控制命令统一通过已验证的 Output + Feature 轮询链路读取最终 ACK。 */
  private async sendControlCommand(
    command: Uint8Array,
    minimumResponseLength: number,
    errorMessage: string,
  ): Promise<DataView> {
    // firmware 已自行处理无线链路的命令偏移（createTelinkKeyboardFirmwareVersionCommand），
    // 避免 sendAndWait 自动再加 0x20 导致命令错误。
    const response = await this.device.sendAndWait(
      TELINK_KEYBOARD_HID.controlOutputReportId,
      createTelinkKeyboardControlPayload(command),
      TELINK_KEYBOARD_HID.controlResponseFeatureReportId,
      (data) => data.getUint8(0) !== TELINK_KEYBOARD_CONTROL_STATUS.pending,
      { skipCmdOffset: true },
    )
    // 轮询结束不代表业务命令成功，仍需检查设备的最终 ACK。
    if (response.getUint8(0) !== TELINK_KEYBOARD_CONTROL_STATUS.acknowledged) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, errorMessage)
    }
    if (response.byteLength < minimumResponseLength) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, `${errorMessage}：响应长度不足`)
    }
    return response
  }

  /**
   * 将键盘返回的 BCD 字节转为点分版本。
   *
   * V700 实际回包按展示顺序在 byte[1] 到 byte[4] 放置版本字段，例如 `01 03 07 00` 表示 V1.3.7。
   * byte[4] 为 0x00 时只是未使用的高位字段，应移除尾部 00，不能倒序后当作主版本号。
   */
  private formatKeyboardVersion(response: DataView): string {
    // 每个字段是独立 BCD 数字，先验证再去掉无意义的尾部高位字段。
    const fields = [
      response.getUint8(1),
      response.getUint8(2),
      response.getUint8(3),
      response.getUint8(4),
    ].map((value) => {
      const high = value >> 4
      const low = value & 0x0f
      if (high > 9 || low > 9) {
        throw new DeviceError(DeviceErrorCode.READ_FAILED, 'Telink 键盘固件版本不是有效 BCD 数据')
      }
      return `${high}${low}`
    })

    while (fields.length > 1 && fields[fields.length - 1] === '00') fields.pop()
    return fields.map((field) => String(Number(field))).join('.')
  }

  /**
   * 从型号目录读取最新版本配置。
   *
   * 固件站点会校验来源页面，因此请求不携带本地页面 Referer，避免下载被站点拒绝。
   */
  private async getVersionInfo(model: string | undefined): Promise<RemoteDeviceVersion | null> {
    if (!model) return null

    // todo 本地/测试环境开发使用
    if (import.meta.env.VITE_BUILD_FLAVOR === 'test') {
      this.device.__setFirmwareBaseUrl('https://soft.rapoo.com/RapooDriver/FirmwareTest')
    }

    const response = await fetch(
      `${this.device.getFirmwareBaseUrl()}/${encodeURIComponent(model)}/${VERSION_FILE_NAME}?t=${Date.now()}`,
      { referrerPolicy: 'no-referrer' },
    )
    if (!response.ok) {
      throw new DeviceError(
        DeviceErrorCode.TIMEOUT,
        `读取 ${model} 在线版本失败：${response.status}`,
      )
    }

    // 服务端版本文件可能包含多个字段，固件能力只消费第一条发布配置。
    const data = (await response.json()) as TelinkFirmwareVersionFile
    const release = data.Device?.[0]
    if (!release) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${model} 在线版本文件缺少 Device 配置`)
    }
    return release
  }

  /**
   * 下载 ZIP 中唯一的 BIN，并按 BIN 头部声明的实际长度截取传输范围。
   *
   * 长度字段位于偏移 0x18，写法来源是 FirmwareUpgradeTelink.cpp::readBinFile()。
   * ZIP 内可能有补齐字节，直接传输压缩包中的全部数据会改变设备计算的块序号和结束边界。
   */
  private async downloadFirmware(model: string | undefined, fileName: string): Promise<Uint8Array> {
    if (!model || !fileName) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件型号或文件名为空')
    }

    const files = await processZip(
      `${this.device.getFirmwareBaseUrl()}/${encodeURIComponent(model)}/${encodeURIComponent(fileName)}`,
      { referrerPolicy: 'no-referrer' },
    )
    // 一个升级包只允许一个 BIN，避免把资源文件或错误型号文件写入设备。
    const entries = Object.values(files).filter(({ extension }) => extension === '.bin')
    if (entries.length !== 1 || entries[0].data.byteLength < 0x1c) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件包中缺少有效 BIN 文件')
    }
    const [entry] = entries

    // BIN 头部的 0x18 保存实际传输长度，ZIP 内的填充字节不能写入设备。
    const size = new DataView(
      entry.data.buffer,
      entry.data.byteOffset,
      entry.data.byteLength,
    ).getUint32(0x18, true)
    if (!Number.isInteger(size) || size <= 0 || size > entry.data.byteLength) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件 BIN 长度字段无效')
    }
    return entry.data.slice(0, size)
  }

  /**
   * 通过正常业务接口通知设备重启到升级程序。
   *
   * 键盘命令来自键盘协议的 `BA FF A8`；接收器命令来自接收器协议的 `BA B1`。
   * 命令会触发软复位，普通 Feature Report 的 ACK 可能在复位前丢失，因此不能以普通 ACK 作为成功条件；
   * 后续能否打开升级接口才是切换成功的依据。
   */
  private async enterFirmwareUpdateMode(isReceiver: boolean): Promise<void> {
    // 先通过正常业务通道切换模式，Bootloader 接口只接受后续升级数据。
    await this.device.channel.sendOutputReport(
      TELINK_KEYBOARD_HID.controlOutputReportId,
      createTelinkKeyboardControlPayload(createTelinkFirmwareUpdateEntryCommand(isReceiver)),
    )
  }

  /**
   * 按 FirmwareUpgradeTelink.cpp::ota_write() 的顺序发送启动帧、数据帧和结束帧。
   *
   * 每次 sendBootloaderPacket() 都会等待 Input Report 回包，任何一帧失败都会中止方法，
   * 防止后续块覆盖在设备未确认的上一块之后。
   */
  private async writeFirmware(updateDevice: BaseDevice, firmware: Uint8Array): Promise<void> {
    // 启动帧被设备确认后，才开始发送不可恢复的数据帧。
    await this.sendBootloaderPacket(updateDevice, this.createOtaStartPacket())

    // 只补齐最后一个不完整数据块，不能额外发送完整的 0xFF 块。
    // FirmwareUpgradeTelink.cpp 在终止分支不再增加 sen[1]，随后以“已发送最后块”的序号构造结束帧；
    // 等价于这里的 ceil()。多发一块会改变 Bootloader 的整包校验并导致结束帧被拒绝。
    const totalBlocks = Math.ceil(firmware.byteLength / OTA_BLOCK_SIZE)
    const paddedFirmware = new Uint8Array(totalBlocks * OTA_BLOCK_SIZE).fill(0xff)
    paddedFirmware.set(firmware)

    for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex += OTA_BLOCKS_PER_REPORT) {
      await this.sendBootloaderPacket(
        updateDevice,
        this.createOtaDataPacket(paddedFirmware, blockIndex, totalBlocks),
      )
      // 进度只在整帧收到回包后推进，避免把尚未被设备接收的数据展示为成功。
      this.reportProgress(((blockIndex + OTA_BLOCKS_PER_REPORT) * 100) / totalBlocks)
    }

    await this.sendBootloaderPacket(updateDevice, this.createOtaEndPacket(totalBlocks - 1))
    this.reportProgress(100)
  }

  /**
   * 创建 OTA 启动帧。
   *
   * 字节含义：`02` 为升级帧类型，`02` 为后续控制数据长度，`00` 为保留字节，
   * `01 FF` 表示设备开始接收固件分块。字段顺序来源：FirmwareUpgradeTelink.cpp::ota_write()。
   */
  private createOtaStartPacket(): Uint8Array {
    const packet = this.createBootloaderPacket()
    packet.set([0x02, 0x02, 0x00, 0x01, 0xff])
    return packet
  }

  /**
   * 创建 OTA 数据帧，每帧最多承载三个“块序号 + 16 字节数据 + CRC16”记录。
   *
   * 帧头中 byte[0] 固定为 0x02，byte[1] 是本帧记录数据长度，byte[2] 为保留字节。
   * 每条记录从 byte[3] 开始连续排列，格式和来源见 FirmwareUpgradeTelink.cpp::ota_write()。
   */
  private createOtaDataPacket(
    firmware: Uint8Array,
    firstBlockIndex: number,
    totalBlocks: number,
  ): Uint8Array {
    const packet = this.createBootloaderPacket()
    packet.set([0x02, 0x00, 0x00])

    // 最后一帧可能不足三块，长度字段必须只统计真实放入的记录。
    const blockCount = Math.min(OTA_BLOCKS_PER_REPORT, totalBlocks - firstBlockIndex)
    for (let offset = 0; offset < blockCount; offset += 1) {
      const blockIndex = firstBlockIndex + offset
      const recordOffset = 3 + offset * OTA_BLOCK_RECORD_SIZE
      const block = firmware.slice(blockIndex * OTA_BLOCK_SIZE, (blockIndex + 1) * OTA_BLOCK_SIZE)
      packet[recordOffset] = blockIndex & 0xff
      packet[recordOffset + 1] = (blockIndex >> 8) & 0xff
      packet.set(block, recordOffset + 2)
      // CRC 覆盖块序号与 16 字节数据，接收端可据此拒绝错序或损坏的块。
      const crc = this.calculateCrc16(packet.slice(recordOffset, recordOffset + 18))
      packet[recordOffset + 18] = crc & 0xff
      packet[recordOffset + 19] = (crc >> 8) & 0xff
    }
    packet[1] = blockCount * OTA_BLOCK_RECORD_SIZE
    return packet
  }

  /**
   * 创建 OTA 结束帧。
   *
   * `02 FF` 是结束控制标识；随后写入最后块序号及其 16 位补码，
   * 设备可借此确认主机认为的传输边界。字段顺序来源：FirmwareUpgradeTelink.cpp::ota_write()。
   */
  private createOtaEndPacket(lastBlockIndex: number): Uint8Array {
    const packet = this.createBootloaderPacket()
    const complement = (0x1_0000 - lastBlockIndex) & 0xffff
    packet.set([
      0x02,
      0x06,
      0x00,
      0x02,
      0xff,
      lastBlockIndex & 0xff,
      (lastBlockIndex >> 8) & 0xff,
      complement & 0xff,
      (complement >> 8) & 0xff,
    ])
    return packet
  }

  /**
   * 创建不含 Report ID 的 63 字节 Bootloader 数据区。
   *
   * 未使用区域固定填充 0xFF，避免设备把零填充解析为额外的有效数据；
   * 0x05 由 sendBootloaderPacket() 作为 WebHID 的 Report ID 单独发送。
   */
  private createBootloaderPacket(): Uint8Array {
    return new Uint8Array(BOOTLOADER_REPORT_DATA_SIZE).fill(0xff)
  }

  /**
   * 发送单帧 Bootloader 数据并等待同 Report ID 的回包。
   *
   * 写法对应 FirmwareUpgradeTelink.cpp::sendCmd() 的“写入后读取”流程；
   * sendAndReceive() 会先注册监听再发送，避免设备回包过快时遗漏事件。
   */
  private async sendBootloaderPacket(updateDevice: BaseDevice, packet: Uint8Array): Promise<void> {
    // sendAndReceive 会先监听 Input Report 再发送，避免设备回包过快导致事件丢失。
    const response = await updateDevice.sendAndReceive(
      BOOTLOADER_REPORT_ID,
      packet,
      BOOTLOADER_REPORT_ID,
      3_000,
    )
    this.assertBootloaderResponse(response.data)
  }

  /**
   * 仅结束帧回包携带最终提交状态；其他回包只能说明本帧已被 Bootloader 接收。
   *
   * 来源：FirmwareUpgradeTelink.cpp::readFileData()。原生 hidapi 回包含 Report ID，
   * WebHID 的 DataView 不含 Report ID，因此同时兼容两种字节偏移以保持校验语义一致。
   */
  private assertBootloaderResponse(response: DataView): void {
    const reportIdOffset =
      response.byteLength > 0 && response.getUint8(0) === BOOTLOADER_REPORT_ID ? 1 : 0
    const finalResponseLength = reportIdOffset + 6
    if (response.byteLength < finalResponseLength) return

    const isFinalResponse =
      response.getUint8(reportIdOffset) === 0x02 &&
      response.getUint8(reportIdOffset + 1) === 0x03 &&
      response.getUint8(reportIdOffset + 2) === 0x00 &&
      response.getUint8(reportIdOffset + 3) === 0x06 &&
      response.getUint8(reportIdOffset + 4) === 0xff
    if (!isFinalResponse) return

    const status = response.getUint8(reportIdOffset + 5)
    if (status !== 0x00) {
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `Telink Bootloader 拒绝提交固件，状态码：0x${status.toString(16).padStart(2, '0')}`,
      )
    }
  }

  /**
   * 计算 OTA 记录的 Modbus CRC16。
   *
   * 算法和初始值 0xFFFF 对应 FirmwareUpgradeTelink.cpp::crc16()；CRC 覆盖块序号和 16 字节数据。
   */
  private calculateCrc16(data: Uint8Array): number {
    let crc = 0xffff
    for (const value of data) {
      let byte = value
      for (let bit = 0; bit < 8; bit += 1) {
        crc = (crc >> 1) ^ ((crc ^ byte) & 0x01 ? 0xa001 : 0)
        byte >>= 1
      }
    }
    return crc
  }

  /**
   * 通过已有固件进度事件通知 Web 层。
   *
   * 进度在一帧收到回包后才上报，因此页面百分比代表设备已回应的帧，而不是主机已尝试发送的帧。
   */
  private reportProgress(progress: number): void {
    // 固件页面和其他消费者已监听该通用事件，不新增 Telink 专用事件名称。
    this.device.emit(FirmwareEvents.WritedData, {
      progress: Math.floor(Math.min(100, Math.max(0, progress))),
    })
  }

  /**
   * 发送可读的升级阶段事件。
   *
   * 测试页使用该事件区分“包已下载”“正在写入”“已发送重启帧”；
   * “设备已重启”不能由这里宣布，必须由页面重新读取版本后确认。
   */
  private reportStage(stage: FirmwareUpdateStage, message: string, progress?: number): void {
    this.device.emit(FirmwareEvents.Progress, { stage, message, progress })
  }

  /** 当前控制通道是接收器时，版本与升级目标均按无线链路处理。 */
  private isReceiverConnection(): boolean {
    return this.device.getConnectionType() === ConnectionType.Wireless
  }
}
